import useFetchArticle from 'app/hooks/fetch/useFetchArticle';
import { ChatItemKBEntry, KBEntryViewType } from 'app/services/chatBot';
import useHandleSearchResultClick from 'app/modules/tailor/hooks/useHandleSearchResultClick';
import OstroDynamicContent from 'app/components/OstroDynamicContent';
import TailorPDFTag from 'app/modules/tailor/components/TailorPDFTag';
import usePdfTagsEnabled from 'app/modules/tailor/hooks/usePdfTagsEnabled';
import { checkIsPdf } from 'app/modules/tailor/hooks/useLinks';
import { highlightText, SearchResultIcon } from 'app/modules/tailor/components/TailorSearchResults';
import { Copy } from 'app/components/Typography';
import ExternalLinkIcon from 'app/components/icons/ExternalLinkIcon';
import { cx } from '@twind/core';
import { useTranslation } from 'react-i18next';
import { PosthogEventName } from 'app/copied/constants/PosthogEventName';
import { getTopicType } from 'app/services/search';

import MessageBase from './BaseChatItem';
import useVirtualAssistantAvatar from '../../hooks/useVirtualAssistantAvatar';
import { useVirtualAssistantStore } from '../../store/virtualAssistantStore';

export default function KBEntryChatItem({
  chatItem,
  className,
  highlightTerms,
}: {
  chatItem: ChatItemKBEntry;
  className?: string;
  highlightTerms?: string;
}) {
  const avatarURL = useVirtualAssistantAvatar();
  const pdfTagsEnabled = usePdfTagsEnabled();
  const { data: article, isLoading } = useFetchArticle(chatItem.kb_constant_id);
  const isPDF = checkIsPdf(article?.resource_link || article?.external_link || '');
  const showPDFTag = pdfTagsEnabled && isPDF;
  const handleClick = useHandleSearchResultClick();
  const { t } = useTranslation();
  const trackKBEntryClicked = useVirtualAssistantStore((state) => state.trackKBEntryClicked);

  const onClickArticle = (retryCount = 0) => {
    if (isLoading) {
      if (retryCount < 2) {
        setTimeout(() => onClickArticle(retryCount + 1), 100);
      }
      return;
    }

    if (!article) {
      return;
    }

    const resultIndex = (chatItem as ChatItemKBEntry).resultIndex ?? 0;
    handleClick({
      result: article,
      resultIndex: resultIndex + 1,
      isSuggested: false,
      skipAnalytics: true,
      linkLocation: 'relatedResource',
    });
    trackKBEntryClicked({
      queryTyped: chatItem.queryTyped || '',
      resultIndex: resultIndex + 1,
      topicSource: chatItem.topicSource,
      topicType: getTopicType(article),
      topicTitle: article.title,
      kbEntryType:
        chatItem.view_type === KBEntryViewType.SOURCE
          ? PosthogEventName.TopicSourceClicked
          : PosthogEventName.RelatedResourcesClicked,
      isConvAiChatItem: chatItem.aiResponse,
      linkUrl: article.resource_link || article.external_link,
    });
  };

  if (!article) {
    return null;
  }

  if (chatItem.view_type === KBEntryViewType.SOURCE) {
    return (
      <MessageBase
        showAvatar={false}
        avatarURL={avatarURL}
        isUserMessage={false}
        className={cx('ostro-chat-item ostro-kb-entry-chatitem-source', className)}
      >
        <Copy
          component="a"
          href={article.resource_link || article.external_link}
          target="_blank"
          className="text-typography-info flex items-center text-xs cursor-pointer"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            onClickArticle();
          }}
        >
          <div className="line-clamp-1">
            <span className="mr-1">{t('virtual_assistant.source')}:</span>
            <OstroDynamicContent
              className="inline text-xs"
              component={Copy}
              content={article.title}
            />
          </div>
          <ExternalLinkIcon className="ml-1 w-3 h-3 flex-shrink-0 text-typography-info" />
        </Copy>
      </MessageBase>
    );
  }

  return (
    <MessageBase
      avatarURL={avatarURL}
      showAvatar={false}
      isUserMessage={false}
      className={cx('ostro-chat-item ostro-kb-entry-chatitem-card -mt-2', className)}
    >
      <button
        onClick={() => onClickArticle()}
        className="border-[0.5px] rounded py-3 px-4 flex justify-between cursor-pointer gap-4 text-left w-full"
        aria-label={`Open article: ${chatItem.title}`}
      >
        <div>
          <div className="ostro-kb-entry-chatitem-title">
            <OstroDynamicContent
              component="span"
              className="line-clamp-2 !font-semibold [&_p]:inline leading-1 inline"
              content={chatItem.title}
            />
            {showPDFTag && <TailorPDFTag className="text-[8px] inline align-middle" />}
          </div>
          <OstroDynamicContent
            className="ostro-kb-entry-chatitem-description text-typography-description line-clamp-2 !text-xs"
            content={highlightText(chatItem.description, highlightTerms)}
          />
        </div>
        <SearchResultIcon
          secondColumnClass="items-center"
          result={article}
        />
      </button>
    </MessageBase>
  );
}
